/**
 * Human-readable relative time phrasing ("today", "2 days ago", "3 months ago").
 *
 * Buckets:
 *   < 1 min       → "just now"
 *   same day      → "today"
 *   1 day         → "yesterday"
 *   2..6 days     → "N days ago"
 *   7..29 days    → "N weeks ago"
 *   30..364 days  → "N months ago"
 *   >= 365 days   → "N years ago"
 */
export function relativeTime(input: string | number | Date | null | undefined): string {
  if (input == null) return 'never';
  const d = input instanceof Date ? input : new Date(input);
  if (Number.isNaN(d.getTime())) return 'never';

  const now = new Date();
  const diffMs = now.getTime() - d.getTime();
  if (diffMs < 60_000) return 'just now';

  const startOfDay = (x: Date) =>
    new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const dayDiff = Math.floor((startOfDay(now) - startOfDay(d)) / 86_400_000);

  if (dayDiff <= 0) return 'today';
  if (dayDiff === 1) return 'yesterday';
  if (dayDiff < 7) return `${dayDiff} days ago`;
  if (dayDiff < 30) {
    const w = Math.floor(dayDiff / 7);
    return w === 1 ? '1 week ago' : `${w} weeks ago`;
  }
  if (dayDiff < 365) {
    const m = Math.floor(dayDiff / 30);
    return m === 1 ? '1 month ago' : `${m} months ago`;
  }
  const y = Math.floor(dayDiff / 365);
  return y === 1 ? '1 year ago' : `${y} years ago`;
}

/**
 * Verbose relative formatter that keeps minute/hour resolution inside the
 * same-day bucket. Suited to security/audit views where "today" is too coarse.
 *
 * Buckets:
 *   < 10 sec      → "just now"
 *   < 1 min       → "N seconds ago"
 *   < 1 hour      → "N min(s) ago"
 *   < 1 day       → "N hour(s) ago"
 *   < 7 days      → "N day(s) ago"
 *   < 30 days     → "N week(s) ago"
 *   < 365 days    → "N month(s) ago"
 *   >= 365 days   → "N year(s) ago"
 *   future dates  → "in N min" / etc.
 */
export function relativeTimeVerbose(
  input: string | number | Date | null | undefined,
): string {
  if (input == null) return 'never';
  const d = input instanceof Date ? input : new Date(input);
  if (Number.isNaN(d.getTime())) return 'never';

  const diffMs = Date.now() - d.getTime();
  const future = diffMs < 0;
  const abs = Math.abs(diffMs);
  if (!future && abs < 10_000) return 'just now';

  const s = Math.floor(abs / 1000);
  const m = Math.floor(s / 60);
  const h = Math.floor(m / 60);
  const days = Math.floor(h / 24);

  const fmt = (n: number, singular: string, plural: string) =>
    `${n} ${n === 1 ? singular : plural}`;

  let phrase: string;
  if (s < 60) phrase = fmt(s, 'second', 'seconds');
  else if (m < 60) phrase = fmt(m, 'min', 'mins');
  else if (h < 24) phrase = fmt(h, 'hour', 'hours');
  else if (days < 7) phrase = fmt(days, 'day', 'days');
  else if (days < 30) phrase = fmt(Math.floor(days / 7), 'week', 'weeks');
  else if (days < 365) phrase = fmt(Math.floor(days / 30), 'month', 'months');
  else phrase = fmt(Math.floor(days / 365), 'year', 'years');

  return future ? `in ${phrase}` : `${phrase} ago`;
}
